import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { postCreatePrepayRequest } from '../../../queries/account';
import { postPaymentData } from '../../../queries/loanPayment';
import jwtDecode from '../../../utils/jwtDecode';
import { Currency } from '../../../types/account';
import analytics from '../../../utils/analytics';
import buildDuemintPaymentUrl from '../../../utils/buildDuemintPaymentUrl';
import bancameLogo from /* webpackPreload: true */ '../../../assets/bancame_logo_dark.svg';

const ALERT_ICON_COLOR = '#8A6100';
const ALERT_TITLE_COLOR = '#5C4100';
const CLOSE_HOVER_BACKGROUND = '#EAE7F8';

const TRANSITION = 'all .2s ease-out';
const BLOCK_STYLE = {
  display: 'flex',
  gap: '12px',
  padding: '14px',
  borderRadius: '16px',
};

type CreatePrepayRequestDialogProps = {
    token: string,
    pprSimulationError: boolean,
    loanId: string,
    currency: Currency,
    hasPendingCharge: boolean,
    expiryTime?: string,
}

const defaultProps = {
  expiryTime: '23:59',
};

export default function CreatePrepayRequestDialog(props: CreatePrepayRequestDialogProps) {
  const {
    token, pprSimulationError, loanId, currency, hasPendingCharge, expiryTime,
  } = props;
  const payload = jwtDecode(token);
  const dialogPageName = hasPendingCharge
    ? 'DIALOGO PREPAGAR CREDITO - COBRO EN PROCESO'
    : 'DIALOGO PREPAGAR CREDITO';
  const [open, setOpen] = React.useState(false);
  const [postPPRError, setPOSTPPRError] = React.useState(false);
  const [pprLoading, setPPRLoading] = React.useState(false);

  const handleOpen = () => {
    analytics.page('PU - TU CREDITO', dialogPageName);
    setOpen(true);
  };

  const handleClose = () => {
    analytics.track('Cerrar Dialog', { initiator: 'usuario' });
    setOpen(false);
  };

  const createPrepayRequest = async () => {
    analytics.track('Click Boton', { text: 'Continuar', initiator: 'usuario' });
    setPPRLoading(true);
    try {
      await postCreatePrepayRequest(token, loanId);
      const { data } = await postPaymentData({
        idNumber: payload.idNumber,
        captchaToken: 'any-value',
      });
      const rawUrl = data.loansDetails.collectionServiceUrl;
      const paymentUrl = buildDuemintPaymentUrl(rawUrl, currency);
      if (!paymentUrl || !currency) {
        setPOSTPPRError(true);
        return;
      }
      window.location.href = paymentUrl;
    } catch (e) {
      setPOSTPPRError(true);
      setPPRLoading(false);
    }
  };

  return (
    <>
      <Button
        disabled={pprSimulationError}
        variant="contained"
        style={{ marginTop: 50, marginBottom: 70 }}
        onClick={handleOpen}
      >
        Prepagar crédito
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth={false}
        aria-labelledby="prepay-dialog-title"
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: '420px',
            margin: '16px',
            backgroundColor: 'white.main',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 24px 48px -12px rgba(51,38,117,0.45)',
          },
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          padding: '18px 20px 16px',
        }}
        >
          <object data={bancameLogo} height="22px" aria-labelledby="banca.me logo" />
          <IconButton
            onClick={handleClose}
            disabled={pprLoading}
            aria-label="Cerrar"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '9999px',
              backgroundColor: 'gray.main',
              color: alpha('#000000', 0.5),
              transition: TRANSITION,
              '&:hover': {
                backgroundColor: CLOSE_HOVER_BACKGROUND,
                color: 'primary.main',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: 'gray.main' }} />

        <Box sx={{
          padding: '22px 24px 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '18px',
        }}
        >
          {hasPendingCharge && (
            <Box sx={{
              ...BLOCK_STYLE,
              backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.16),
            }}
            >
              <ErrorIcon sx={{ fontSize: 20, color: ALERT_ICON_COLOR }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography sx={{
                  fontSize: '13.5px', fontWeight: 700, lineHeight: 1.3, color: ALERT_TITLE_COLOR,
                }}
                >
                  Cobro en proceso
                </Typography>
                <Typography sx={{
                  fontSize: '12.5px', lineHeight: 1.45, color: alpha('#000000', 0.7),
                }}
                >
                  Hay un cobro en curso a la cuenta asociada a tu
                  {' '}
                  <Typography variant="inherit" component="span" fontWeight={700}>PAC</Typography>
                  . Si pagas ahora, el monto podría duplicarse; si eso pasa,
                  te devolvemos el cobro extra.
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              id="prepay-dialog-title"
              component="h3"
              sx={{
                fontFamily: 'Aeonik',
                fontSize: '22px',
                fontWeight: 400,
                lineHeight: 1.15,
                color: 'primary.main',
                margin: 0,
              }}
            >
              Prepaga tu crédito
            </Typography>
            <Typography sx={{
              fontSize: '14.5px', lineHeight: 1.45, color: alpha('#000000', 0.7),
            }}
            >
              Al presionar
              {' '}
              <Typography variant="inherit" component="span" fontWeight={700} color="black.main">continuar</Typography>
              {' '}
              te llevamos al Portal de Pagos para completar el prepago.
            </Typography>
          </Box>

          <Box sx={{ ...BLOCK_STYLE, backgroundColor: 'gray.main' }}>
            <ScheduleIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ fontSize: '13.5px', fontWeight: 700, lineHeight: 1.3 }}>
                Vigente hasta las
                {' '}
                {expiryTime}
                {' '}
                hrs de hoy
              </Typography>
              <Typography sx={{
                fontSize: '12.5px', lineHeight: 1.45, color: alpha('#000000', 0.55),
              }}
              >
                Si la solicitud vence, puedes generar una nueva cuando quieras.
              </Typography>
            </Box>
          </Box>

          {postPPRError && (
            <Typography color="error" variant="caption" component="div">
              Ha ocurrido un error creando la solicitud de prepago, por favor contáctanos
            </Typography>
          )}
        </Box>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '8px',
          padding: '20px 24px 24px',
        }}
        >
          <Button
            onClick={handleClose}
            disabled={pprLoading}
            sx={{
              height: '45px',
              width: 'auto',
              minWidth: 0,
              flexShrink: 0,
              padding: '0 20px',
              borderRadius: '10px',
              backgroundColor: 'transparent',
              fontSize: '14.5px',
              fontWeight: 700,
              color: 'black.main',
              transition: TRANSITION,
              '&:hover, &:focus-visible': {
                color: 'secondary.main',
                backgroundColor: 'gray.main',
              },
            }}
          >
            Cerrar
          </Button>
          <Button
            onClick={createPrepayRequest}
            disabled={pprLoading}
            endIcon={pprLoading
              ? <CircularProgress size={18} color="inherit" />
              : <ArrowForwardIcon sx={{ fontSize: 18 }} />}
            sx={{
              height: '45px',
              width: 'auto',
              minWidth: 0,
              flexShrink: 0,
              padding: '0 26px',
              borderRadius: '10px',
              gap: '8px',
              '& .MuiButton-endIcon': { marginLeft: 0, marginRight: 0 },
              backgroundColor: 'secondary.main',
              fontSize: '14.5px',
              fontWeight: 700,
              color: 'white.main',
              boxShadow: 'none',
              transition: TRANSITION,
              '&:hover, &:focus-visible': {
                backgroundColor: 'primary.main',
                boxShadow: 'none',
              },
            }}
          >
            Continuar
          </Button>
        </Box>
      </Dialog>
      {pprSimulationError && (
      <Typography color="error">
        Ha ocurrido un error cargando la simulación de prepago,
        por favor contáctanos
      </Typography>
      )}
    </>
  );
}

CreatePrepayRequestDialog.defaultProps = defaultProps;
